import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Box, Card, Container, Grid, Tab, Tabs } from '@mui/material';
import { TabsContainerWrapper } from 'src/layouts/TabsContainerWrapper';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import routes from 'src/Resources/routes.json';
import InstallationSearch from './InstallationSearch';
import { FormattedMessage } from 'react-intl';
import { UserContext } from '../../../contexts/userContext';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import ListIcon from '@mui/icons-material/List';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TreeView from '../Tree/treeView';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { UserType } from '../../../interfaces/UserTypes';
import SodioHomeInstallation from './Installation';

interface SodioHomeInstallationTabsProps {
  product: number;
}

function SodioHomeInstallationTabs(props: SodioHomeInstallationTabsProps) {
  const location = useLocation();
  const context = useContext(UserContext);
  const { currentUser } = context;
  const tabList = [
    'live',
    'batteryview',
    'information',
    'manage',
    'overview',
    'log',
    'history',
    'configuration'
  ];

  const [currentTab, setCurrentTab] = useState<string>(undefined);
  const [fetchedInstallations, setFetchedInstallations] =
    useState<boolean>(false);
  const {
    sodiohomeInstallations,
    fetchAllInstallations,
    socket,
    openSocket,
    closeSocket
  } = useContext(InstallationsContext);
  const { product, setProduct } = useContext(ProductIdContext);

  useEffect(() => {
    let path = location.pathname.split('/');

    if (path[path.length - 2] === 'list') {
      setCurrentTab('list');
      fetchAllInstallations(product, false);
    } else if (path[path.length - 2] === 'tree') {
      setCurrentTab('tree');
    } else {
      //Even if we are located at path: /batteryview/mainstats, we want the BatteryViewSalidomo tab to be bold
      setCurrentTab(path.find((pathElement) => tabList.includes(pathElement)));
    }
  }, [location]);

  useEffect(() => {
    setProduct(props.product);
  }, [props.product]);

  useEffect(() => {
    if (product == props.product) {
      fetchAllInstallations(product);
    }
  }, [product]);

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  const navigateToTabPath = (pathname: string, tab_value: string): string => {
    let pathlist = pathname.split('/');
    let ret_path = '';
    for (let i = 1; i < pathlist.length; i++) {
      if (Number.isNaN(Number(pathlist[i]))) {
        ret_path += '/';
        ret_path += pathlist[i];
      } else {
        //When finding the installation id (number), break and then add the tab_value
        ret_path += '/';
        ret_path += pathlist[i];
        ret_path += '/';
        break;
      }
    }

    ret_path += tab_value;
    return ret_path;
  };

  const singleInstallationTabs =
    currentUser.userType == UserType.admin
      ? [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          // {
          //   value: 'overview',
          //   label: <FormattedMessage id="overview" defaultMessage="Overview" />
          // },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },

          {
            value: 'manage',
            label: (
              <FormattedMessage
                id="manage"
                defaultMessage="Access Management"
              />
            )
          },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },

          {
            value: 'configuration',
            label: (
              <FormattedMessage
                id="configuration"
                defaultMessage="Configuration"
              />
            )
          },
          {
            value: 'history',
            label: (
              <FormattedMessage
                id="history"
                defaultMessage="History Of Actions"
              />
            )
          }
        ]
      : [
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          // {
          //   value: 'overview',
          //   label: <FormattedMessage id="overview" defaultMessage="Overview" />
          // },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          }
        ];

  const tabs =
    currentTab != 'list' &&
    currentTab != 'tree' &&
    !location.pathname.includes('folder') &&
    currentUser.userType == UserType.admin
      ? [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          },
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'batteryview',
            label: (
              <FormattedMessage
                id="batteryview"
                defaultMessage="Battery View"
              />
            )
          },
          // {
          //   value: 'overview',
          //   label: <FormattedMessage id="overview" defaultMessage="Overview" />
          // },
          {
            value: 'log',
            label: <FormattedMessage id="log" defaultMessage="Log" />
          },

          {
            value: 'manage',
            label: (
              <FormattedMessage
                id="manage"
                defaultMessage="Access Management"
              />
            )
          },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          },
          {
            value: 'configuration',
            label: (
              <FormattedMessage
                id="configuration"
                defaultMessage="Configuration"
              />
            )
          },
          {
            value: 'history',
            label: (
              <FormattedMessage
                id="history"
                defaultMessage="History Of Actions"
              />
            )
          }
        ]
      : currentTab != 'list' &&
        currentTab != 'tree' &&
        !location.pathname.includes('folder') &&
        currentUser.userType == UserType.client
      ? [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          },
          {
            value: 'live',
            label: <FormattedMessage id="live" defaultMessage="Live" />
          },
          {
            value: 'overview',
            label: <FormattedMessage id="overview" defaultMessage="Overview" />
          },

          {
            value: 'information',
            label: (
              <FormattedMessage id="information" defaultMessage="Information" />
            )
          }
        ]
      : [
          {
            value: 'list',
            icon: <ListIcon id="mode-toggle-button-list-icon" />
          },
          {
            value: 'tree',
            icon: <AccountTreeIcon id="mode-toggle-button-tree-icon" />
          }
        ];

  return sodiohomeInstallations.length > 1 ? (
    <>
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                component={Link}
                label={tab.label}
                to={
                  tab.value === 'list' || tab.value === 'tree'
                    ? routes[tab.value]
                    : navigateToTabPath(location.pathname, routes[tab.value])
                }
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={routes.list + '*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <InstallationSearch
                        installations={sodiohomeInstallations}
                      />
                    </Box>
                  </Grid>
                }
              />

              <Route path={routes.tree + '*'} element={<TreeView />} />

              <Route
                path={'*'}
                element={
                  <Navigate
                    to={routes.sodiohome_installations + routes.list}
                  ></Navigate>
                }
              ></Route>
            </Routes>
          </Grid>
        </Card>
      </Container>
    </>
  ) : sodiohomeInstallations.length === 1 ? (
    <>
      {' '}
      <Container maxWidth="xl" sx={{ marginTop: '20px' }} className="mainframe">
        <TabsContainerWrapper>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {singleInstallationTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                component={Link}
                label={tab.label}
                to={routes[tab.value]}
              />
            ))}
          </Tabs>
        </TabsContainerWrapper>
        <Card variant="outlined">
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={0}
          >
            <Routes>
              <Route
                path={'*'}
                element={
                  <Grid item xs={12}>
                    <Box p={4}>
                      <SodioHomeInstallation
                        current_installation={sodiohomeInstallations[0]}
                        type="installation"
                      ></SodioHomeInstallation>
                    </Box>
                  </Grid>
                }
              />
            </Routes>
          </Grid>
        </Card>
      </Container>
    </>
  ) : null;
}

export default SodioHomeInstallationTabs;
